import React, { useEffect, useState } from "react";
import Title from "../../components/Title";
import { DataGrid } from "@material-ui/data-grid";
import { escapeRegExp } from "../../utils";
import VehicleService from "../../services/VehicleService";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DirectionService from "../../services/DirectionService";
import { useNavigate } from "react-router";
import {
  Autocomplete,
  DialogActions,
  FormControl,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Spacer from "../../components/Spacer";
import Message from "../tools/Message";
import LoadingSimpleBackdrop from "../tools/LoadingSimpleBackdrop";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
    elevation: 0,
  },
}));

const VehicleTable = () => {
  const classes = useStyles();
  const now = new Date();
  const userDirection = localStorage.getItem("USER_DIR")
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [imms, setImms] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] =
    useState(true);
  const [directions, setDirections] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [filter, setFilter] = useState({
    immatriculation: "",
    driver: "",
    direction: userDirection ? userDirection : "",
  });
  const navigate = useNavigate();
  const [message, setMessage] = React.useState({
    open: false,
    color: "green",
    messageInfo: "",
  });
  const { open, color, messageInfo } = message;

  function handleCloseMessage() {
    setMessage({ ...message, open: false });
  }

   const formatDate = (dateString) => {
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  useEffect(() => {

     async function fetchVehicle() {
      setOpenLoadingSimpleBackdrop(true);
      try {
          if (userDirection) {
          handleFilter();
        } else {
        const result = await VehicleService.getAllVehicles();
        if (result?.data) {
          setImms(result.data.map((vehicle) => vehicle.vhc_imm));
          setVehicles(
            result.data.map((vehicle) => ({
              ...vehicle,
              id: vehicle.vhc_id,
              immatriculation: vehicle.vhc_imm,
              rental_start_date: vehicle.rental_start_date ? formatDate(vehicle.rental_start_date) : "",
              rental_end_date: vehicle.rental_end_date ? formatDate(vehicle.rental_end_date) : "",
            }))
          );
        }
      }
      } catch (error) {
        console.error("Error fetching drivers:", error);
      } finally {
        setOpenLoadingSimpleBackdrop(false);
      }
    }

    async function fetchDrivers() {
      setOpenLoadingSimpleBackdrop(true);
      try {
        const result = await VehicleService.getDriversMission();
        if (result?.data) {
          setDrivers(result.data);
        }
      } catch (error) {
        console.error("Error fetching drivers:", error);
      } finally {
        setOpenLoadingSimpleBackdrop(false);
      }
    }

    async function fetchDirections() {
      setOpenLoadingSimpleBackdrop(true);
      try {
        DirectionService.getDirection().then((result) => {
          if (result && result.data) {
            setDirections(result.data);
          }
        });
      } catch (error) {
        console.error("Error fetching directions:", error);
      } finally {
        setOpenLoadingSimpleBackdrop(false);
      }
    }

    fetchDirections();
    fetchDrivers();
    fetchVehicle();
  }, []);

  const handleClick = () => {
    navigate("/Logistic/mission/vehicle/add/");
  };
  const handleOpenDialog = (id) => {
    setSelectedVehicle(id);
    setOpenDialog(true);
  };

  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDelete = () => {
    setOpenLoadingSimpleBackdrop(true);

    try {
      VehicleService.deleteVehicle(selectedVehicle).then((result) => {
        if (result && result.data) {
          const newRows = vehicles.filter(
            (vehicle) => vehicle.id !== selectedVehicle
          );
          setVehicles(newRows);
          setOpenDialog(false);
          setMessage({
            open: true,
            color: "green",
            messageInfo: "Véhicule supprimé avec succès",
          });
          setOpenLoadingSimpleBackdrop(false);
        } else {
          setOpenLoadingSimpleBackdrop(false);
        }
      });
    } catch (error) {
      console.error("Error deleting vehicle:", error);
      setMessage({
        open: true,
        color: "red",
        messageInfo: "Erreur lors de la suppression du véhicule",
      });
      setOpenLoadingSimpleBackdrop(false);
    }
  };

  const handleCloseDialog = () => {
    setSelectedVehicle(null);
    setOpenDialog(false);
  };

  const handleChangeFilter = (e) => {
    const { name, value } = e.target;
    setFilter({ ...filter, [name]: value });
  };

  const handleFilter = () => {
     setOpenLoadingSimpleBackdrop(true);
    VehicleService.filterVehicles(filter).then((result) => {
      if (result && result.data) {
        setVehicles(
          result.data.map((vehicle) => ({
             ...vehicle,
            id: vehicle.vhc_id,
            immatriculation: vehicle.vhc_imm,
            rental_start_date: vehicle.rental_start_date ? formatDate(vehicle.rental_start_date) : "",
            rental_end_date: vehicle.rental_end_date ? formatDate(vehicle.rental_end_date) : "",
          }))
        );
        setOpenLoadingSimpleBackdrop(false);
      } else {
        setOpenLoadingSimpleBackdrop(false);
      }
    });
  };

  const columns = [
    { field: "vhc_imm", headerName: "Immatriculation", width: 200 },
    {
      field: "direction",
      headerName: "Direction",
      width: 120,
      renderCell: (params) => {
        const direction = directions.find(
          (direction) => direction.direction_id === params.row.direction_id
        );
        return direction ? direction.code : "";
      },
    },
    {
      field: "driver",
      headerName: "Chauffeur",
      width: 320,
      renderCell: (params) => {
        if (params.row.vhc_type === "FID") {
          const driver = drivers.find(
            (driver) => driver.user_id === params.row.driver_id
          );
          return driver ? `${driver.name}` : "";
        } else {
          return (
           params.row.driver_contact ? params.row.driver_name + " (" + params.row.driver_contact + ")" : params.row.driver_name
          );
        }
      },
    },
    { field: "vhc_type", headerName: "Type", width: 120 },
    {
      field: "vhc_mark",
      headerName: "Marque",
      width: 140,
      renderCell: (params) => {
        return params.row.vhc_mark.toUpperCase();
      },
    },
    { field: "vhc_energy", headerName: "Source d'énergie", width: 200 },
    {
      field: "vhc_conso_100km",
      headerName: "Consommation (L/100 km) ",
      width: 260,
    },
    { field: "vhc_description", headerName: "Description", width: 250 },
    { field: "vhc_place", headerName: "Nombre des places", width: 220 },
    { field: "rental_start_date", headerName: "Date de début location", width: 240},
  { 
    field: "rental_end_date", 
    headerName: "Date de fin location", 
    width: 220},
    {
      field: "vhc_status",
      headerName: "Action",
      width: 140,
      renderCell: (params) => {
        return (
          <div>
            { params.row.rental_end_date < now.toISOString().split("T")[0] && params.row.type == "Location" ? ("") : (
            <Button
              onClick={() => {
                navigate("/Logistic/mission/vehicle/edit/" + params.row.id);
              }}
              size="small"
              sx={{ marginRight: "20px" }}
              title="Modifier voiture"
            >
              <EditIcon />
            </Button>
            )}
            <Button
              onClick={() => {
                handleOpenDialog(params.row.id);
              }}
              size="small"
              color="secondary"
              title="Supprimer voiture"
            >
              <DeleteIcon />
            </Button>
          </div>
        );
      },
    },
  ];

  const handleChangeAutocomplete = (event, name, value) => {
    event.preventDefault();
    setFilter({
      ...filter,
      [name]:
        name == "direction"
          ? value.direction_id
          : name == "immatriculation"
          ? value
          : value.user_id,
    });
  };
  return (
    <>
    <LoadingSimpleBackdrop open={openLoadingSimpleBackdrop} />
      <Message
        open={open}
        color={color}
        message={messageInfo}
        onClose={handleCloseMessage}
      />
      <Grid container item xs={12}>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleClick}
          style={{ fontWeight: 600 }}
        >
          CREER
        </Button>
        <Spacer x={2} />
        <Button
          variant="contained"
          color="primary"
          onClick={handleFilter}
          className={classes.btn}
        >
          <SearchIcon style={{ marginRight: "8px" }} />
          Filtrer
        </Button>
      </Grid>
      <Spacer y={1} />
      <Grid container xs={12} spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <label className={classes.labelPosition}>Immatriculation:</label>
          <FormControl fullWidth variant="outlined" size="small">
            <Autocomplete
              id="tags-outlined"
              options={imms}
              getOptionLabel={(option) => (option ? option : "")}
              filterSelectedOptions
              onChange={(event, value) =>
                handleChangeAutocomplete(event, "immatriculation", value)
              }
              value={filter.immatriculation}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  id="outlined-basic"
                  size="small"
                  variant="outlined"
                  label="Immatriculation"
                />
              )}
            />
            {/* <TextField
                        fullWidth
                        id="immatriculation"
                        size="small"
                        variant="outlined"
                        name="immatriculation"
                      onChange={handleChangeFilter}
                      /> */}
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          Chauffeur :
          <Autocomplete
            id="tags-outlined"
            options={drivers}
            getOptionLabel={(option) => (option ? option.name : "")}
            filterSelectedOptions
            onChange={(event, value) =>
              handleChangeAutocomplete(event, "driver", value)
            }
            value={drivers.find((driver) => driver.user_id == filter.driver)}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                id="outlined-basic"
                size="small"
                variant="outlined"
                label="Chauffeur"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          Direction:
          <Autocomplete
            id="tags-outlined"
            options={directions}
            getOptionLabel={(option) =>
              option ? option.name + "(" + option.code + ")" : ""
            }
            filterSelectedOptions
            onChange={(event, value) =>
              handleChangeAutocomplete(event, "direction", value)
            }
            value={directions.find(
              (direction) => direction.direction_id == filter.direction
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                id="outlined-basic"
                size="small"
                variant="outlined"
                label="Direction"
              />
            )}
          />
        </Grid>
      </Grid>
      <Spacer y={2} />
      <Paper className={classes.paper} variant="outlined">
        <Title>Listes des voitures</Title>

        <div style={{ height: "58vh", width: "100%", paddingTop: "1%" }}>
          <DataGrid
            columns={columns}
            rows={vehicles || []}
            pageSize={7}
            disableSelectionOnClick
            componentsProps={{
              toolbar: {
                value: searchText,
                onChange: (event) => requestSearch(event.target.value),
                clearSearch: () => requestSearch(""),
              },
            }}
          />
        </div>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle id="form-dialog-title" style={{ color: "white" }}>
          <strong>Supprimer la voiture</strong>
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            Voulez-vous vraiment supprimer cette voiture ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            color="secondary"
            variant="contained"
            size="small"
          >
            Annuler
          </Button>
          <Button
            onClick={handleDelete}
            color="primary"
            variant="contained"
            size="small"
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VehicleTable;
